import React, { useState, useEffect, useCallback } from 'react';
import { Card, Typography, Breadcrumb, Switch, Row, Col, Input, Space, Tag, Spin, Empty, Tooltip, App } from 'antd';
import { SearchOutlined, InfoCircleOutlined, SyncOutlined } from '@ant-design/icons';
import { message } from '../utils/message';
import clientInteractionService, {
  ClientInteractionItem,
  VersionInfo
} from '../services/clientInteractions';

const { Title, Text } = Typography;

const InteractionManagement: React.FC = () => {
  const { message } = App.useApp();

  // 상태 관리
  const [interactionData, setInteractionData] = useState<ClientInteractionItem[]>([]);
  const [versionInfo, setVersionInfo] = useState<VersionInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [filteredData, setFilteredData] = useState<ClientInteractionItem[]>([]);

  // 데이터 로드 함수 (최적화: 단일 API 호출)
  const loadInteractionData = useCallback(async () => {
    setLoading(true);
    try {
      // console.log('=== Loading Client Interactions ===');

      // 목록 조회로 데이터와 통계 정보를 한번에 가져오기
      const interactionResponse = await clientInteractionService.getInteractionList(1, 100);

      // console.log('Interaction Response:', interactionResponse);

      setInteractionData(interactionResponse.data);

      // stats에서 버전 정보 추출
      const stats = interactionResponse.stats;
      if (stats.version) {
        const versionInfo: VersionInfo = {
          version: stats.version,
          totalCount: stats.total,
          activeCount: stats.active,
          inactiveCount: stats.inactive,
          lastUpdated: null
        };
        setVersionInfo(versionInfo);
      } else {
        setVersionInfo(null);
      }

      if (interactionResponse.data.length > 0) {
        message.success('인터랙션 목록을 성공적으로 불러왔습니다.');
      }
    } catch (error) {
      console.error('Failed to load client interactions:', error);
      message.error('인터랙션 목록을 불러오는 중 오류가 발생했습니다.');

      // 빈 데이터로 초기화
      setInteractionData([]);
      setVersionInfo(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    loadInteractionData();
  }, [loadInteractionData]);

  // 검색 필터링
  useEffect(() => {
    if (!searchKeyword.trim()) {
      setFilteredData(interactionData);
    } else {
      const filtered = interactionData.filter(item =>
        item.title.toLowerCase().includes(searchKeyword.toLowerCase()) ||
        item.description.toLowerCase().includes(searchKeyword.toLowerCase())
      );
      setFilteredData(filtered);
    }
  }, [interactionData, searchKeyword]);

  // 활성화 상태 토글 핸들러 (유일한 수정 기능)
  const handleToggleActive = async (id: number, checked: boolean) => {
    try {
      // console.log(`=== Toggle Interaction ${id} to ${checked} ===`);

      const updatedItem = await clientInteractionService.toggleInteractionStatus(id, checked);
      // console.log('Toggle response:', updatedItem);

      // 로컬 상태 업데이트
      const updatedData = interactionData.map(item =>
        item.id === id ? updatedItem : item
      );
      setInteractionData(updatedData);

      // 통계 정보 실시간 업데이트
      if (versionInfo) {
        const newActiveCount = updatedData.filter(item => item.isActive).length;
        const newInactiveCount = updatedData.length - newActiveCount;

        setVersionInfo(prev => prev ? {
          ...prev,
          totalCount: updatedData.length,
          activeCount: newActiveCount,
          inactiveCount: newInactiveCount
        } : null);
      }

      message.success(`인터랙션이 ${checked ? '활성화' : '비활성화'}되었습니다.`);
    } catch (error) {
      console.error('Toggle interaction error:', error);
      message.error('상태 변경 중 오류가 발생했습니다.');
    }
  };

  // 검색 핸들러
  const handleSearch = (value: string) => {
    setSearchKeyword(value);
  };

  // 읽기 전용 인터랙션 카드 컴포넌트
  const InteractionCard: React.FC<{ item: ClientInteractionItem; showToggle?: boolean }> = ({ item, showToggle = true }) => (
    <Card
      style={{
        backgroundColor: '#2a2a2a',
        border: '1px solid #404040',
        borderRadius: '8px',
        height: '100%',
      }}
      styles={{ body: { padding: '16px' } }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* 카드 헤더 - 활성화 토글 (단독 실행 아이템은 토글 없음) */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '12px'
        }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Tag color={item.isActive ? 'green' : 'red'} style={{ margin: 0 }}>
              {item.isActive ? '활성' : '비활성'}
            </Tag>
            {item.isStandalone && (
              <Tag color="blue" style={{ margin: 0 }}>
                단독 실행
              </Tag>
            )}
          </div>
          {showToggle && (
            <Switch
              checked={item.isActive}
              onChange={(checked) => handleToggleActive(item.id, checked)}
              size="small"
            />
          )}
        </div>

        {/* 제목 */}
        <Title
          level={4}
          style={{
            color: '#ffffff',
            margin: '0 0 8px 0',
            fontSize: '16px',
          }}
        >
          {item.title}
        </Title>


        {/* 설명 */}
        <div style={{ flex: 1, marginBottom: '12px' }}>
          <Text style={{
            color: '#cccccc',
            fontSize: '14px',
            lineHeight: '1.4',
            display: 'block'
          }}>
            {item.description}
          </Text>
        </div>

      </div>
    </Card>
  );

  // 버전 정보 표시 컴포넌트
  const VersionDisplay: React.FC = () => {
    if (!versionInfo) return null;

    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        padding: '16px',
        backgroundColor: '#1a1a1a',
        border: '1px solid #404040',
        borderRadius: '8px',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <InfoCircleOutlined style={{ color: '#F24822' }} />
          <Text style={{ color: '#ffffff', fontWeight: 'bold' }}>
            현재 버전: {clientInteractionService.formatVersion(versionInfo.version)}
          </Text>
        </div>

        <div style={{ display: 'flex', gap: '16px' }}>
          <Text style={{ color: '#a6a6a6' }}>
            전체: {versionInfo.totalCount}개
          </Text>
          <Text style={{ color: '#F24822' }}>
            활성: {versionInfo.activeCount}개
          </Text>
          <Text style={{ color: '#ff4d4f' }}>
            비활성: {versionInfo.inactiveCount}개
          </Text>
        </div>

        <div style={{ marginLeft: 'auto' }}>
          <Text style={{ color: '#a6a6a6', fontSize: '12px' }}>
            마지막 업데이트: {clientInteractionService.formatLastUpdated(versionInfo.lastUpdated)}
          </Text>
        </div>

        <Tooltip title="데이터 새로고침">
          <SyncOutlined
            style={{ color: '#F24822', cursor: 'pointer' }}
            onClick={loadInteractionData}
          />
        </Tooltip>
      </div>
    );
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* 브레드크럼 */}
      <Breadcrumb
        items={[
          { title: '콘텐츠 관리' },
          { title: '미디어 아트' },
          { title: '인터랙션 관리' },
        ]}
        style={{ marginBottom: '8px' }}
      />

      {/* 페이지 제목 */}
      <Title level={2} style={{ margin: '0 0 16px 0' }}>
        인터랙션 관리
      </Title>

      <Text style={{ color: '#a6a6a6', display: 'block', marginBottom: '16px' }}>
        클라이언트에서 생성된 인터랙션을 관리합니다. 활성화 상태만 변경할 수 있습니다.
      </Text>

      {/* 버전 정보 표시 */}
      <VersionDisplay />

      {/* 검색 */}
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        marginBottom: '16px'
      }}>
        <Input
          placeholder="검색"
          suffix={<SearchOutlined style={{ color: '#a6a6a6' }} />}
          style={{
            width: 400,
            height: 40,
            borderRadius: 8,
            background: '#2a2a2a',
            border: '1px solid #404040',
            color: '#ffffff'
          }}
          value={searchKeyword}
            allowClear
          onChange={(e) => handleSearch(e.target.value)}
        />
      </div>

      {/* 로딩 상태 */}
      {loading && (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: '#a6a6a6'
        }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px' }}>
            인터랙션 목록을 불러오는 중...
          </div>
        </div>
      )}

      {/* 빈 상태 */}
      {!loading && filteredData.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '40px',
        }}>
          <Empty
            description={
              <div style={{ color: '#a6a6a6' }}>
                <div>{clientInteractionService.getEmptyStateMessage()}</div>
                {searchKeyword && (
                  <div style={{ marginTop: '8px', fontSize: '14px' }}>
                    '{searchKeyword}' 검색 결과가 없습니다.
                  </div>
                )}
              </div>
            }
          />
        </div>
      )}

      {/* 카드 그리드 - 두 섹션으로 분리 */}
      {!loading && filteredData.length > 0 && (
        <>
          {/* 단독 실행 콘텐츠 섹션 */}
          {filteredData.some(item => item.isStandalone) && (
            <div style={{ marginBottom: '32px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '16px',
                paddingBottom: '12px',
                borderBottom: '2px solid #404040'
              }}>
                <Title level={4} style={{ margin: 0, color: '#ffffff' }}>
                  단독 실행 콘텐츠
                </Title>
                <Tag color="blue">
                  {filteredData.filter(item => item.isStandalone).length}개
                </Tag>
              </div>
              <Row gutter={[16, 16]}>
                {filteredData
                  .filter(item => item.isStandalone)
                  .map((item) => (
                    <Col key={item.id} xs={24} sm={12} md={8} lg={6}>
                      <InteractionCard item={item} showToggle={false} />
                    </Col>
                  ))}
              </Row>
            </div>
          )}

          {/* 일반 콘텐츠 섹션 */}
          {filteredData.some(item => !item.isStandalone) && (
            <div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '16px',
                paddingBottom: '12px',
                borderBottom: '2px solid #404040'
              }}>
                <Title level={4} style={{ margin: 0, color: '#ffffff' }}>
                  인터랙션 랜덤 콘텐츠
                </Title>
                <Tag color="green">
                  {filteredData.filter(item => !item.isStandalone).length}개
                </Tag>
              </div>
              <Row gutter={[16, 16]}>
                {filteredData
                  .filter(item => !item.isStandalone)
                  .map((item) => (
                    <Col key={item.id} xs={24} sm={12} md={8} lg={6}>
                      <InteractionCard item={item} showToggle={true} />
                    </Col>
                  ))}
              </Row>
            </div>
          )}
        </>
      )}
    </Space>
  );
};

export default InteractionManagement;